<template>
  <div v-show="props.isCompletedStep" class="mb-2 font-medium">
    <ShippingMethodCompleted
      :is-only-special-shipping-method="isOnlySpecialShippingMethod"
      :no-shipping-choices="hasNoShippingChoices"
    />
  </div>
  <div v-show="props.isActiveStep" class="md:-mx-5 md:px-6">
    <div
      v-if="!cartHasUkHeavyItems && !cartHasVaporCells && shipmentMethodTopNote"
      v-html-safe="shipmentMethodTopNote"
      class="mb-2 font-medium leading-6"
    ></div>
    <!-- Channel partner: no freight quote, show EXW message and skip carrier selection -->
    <div v-if="isChannelPartner" class="font-medium leading-6">
      {{ $t("shared.checkout.channel_partner_shipping_message") }}
    </div>
    <div v-else-if="!isOnlySpecialShippingMethod && !hasNoShippingChoices && !shippingChoiceCallError">
      <!-- DHL section -->
      <ShippingMethodDhl
        v-model:selected-shipping-method="selectedShippingMethod"
        :dhl-choices="dhlChoices || []"
        :shipping-account-number="shippingAccountNumber"
        :shipping-account-number-recalculate-totals="shippingAccountNumberRecalculateTotals"
      />

      <!-- FedEx section -->
      <ShippingMethodFedex
        v-model:selected-shipping-method="selectedShippingMethod"
        :fed-ex-choices="fedExChoices || []"
        :shipping-account-number="shippingAccountNumber"
        :shipping-account-number-recalculate-totals="shippingAccountNumberRecalculateTotals"
        :fed-ex-expedite-it="isFedExExpediteIt"
      />

      <!-- UPS Carbon section -->
      <ShippingMethodUps
        v-model:selected-shipping-method="selectedShippingMethod"
        :ups-choices="upsChoices || []"
        :shipping-account-number="shippingAccountNumber"
        :shipping-account-number-recalculate-totals="shippingAccountNumberRecalculateTotals"
      />
    </div>
    <div v-else class="flex flex-col gap-2.5">
      <div v-if="cartHasUkHeavyItems" class="font-medium leading-6">
        {{ $t("shared.checkout.cart_weight_exceeded_case") }}
      </div>
      <div v-if="weightExceeded > 0" class="font-medium leading-6">{{ weightLimitExceeded }}</div>
      <div v-if="cartHasVaporCells" class="mt-2 font-medium leading-6">
        {{ $t("shared.checkout.vapor_cells_case") }}
      </div>
      <div class="font-bold leading-6">{{ $t("shared.checkout.quote_shipping_method") }}</div>
    </div>

    <ShippingCo2Emissions
      v-if="
        showCo2Emissions &&
        !isChannelPartner &&
        !isOnlySpecialShippingMethod &&
        !hasNoShippingChoices &&
        !shippingChoiceCallError
      "
    />
    <ShippingMethodPackages
      v-if="!isChannelPartner"
      v-model:wants-fewer-packages="wantsFewerPackages"
      :currency-settings="currencySettings"
    />

    <ShippingMethodAccountNumber
      v-model:shipping-account-number-recalculate-totals="shippingAccountNumberRecalculateTotals"
      :currency-settings="currencySettings"
    />

    <div v-if="formattedCartWeight" class="mb-2.5 flex flex-col leading-6">
      <p class="text-md font-bold">{{ $t("shared.cart.estimated_shipping_weight") }}</p>
      <p>{{ formattedCartWeight }}</p>
    </div>

    <div class="my-4 flex w-full flex-col gap-4 md:w-fit">
      <TlLoadingButton
        color="primary"
        variant="solid"
        type="submit"
        class="mt-3 min-h-[48px]"
        size="lg"
        :disabled="
          isLoadingShippingChoices ||
          changing ||
          (!isOnlySpecialShippingMethod && !hasNoShippingChoices && !selectedShippingMethod)
        "
        :complete-text="$t(`shared.wishlists.list_card.saved`)"
        @click="nextStepHandle"
      >
        {{ $t("pages.checkout.save_and_continue") }}
      </TlLoadingButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useThemeContext } from "@/core/composables/useThemeContext";
import { globals } from "@/core/globals";
import FeatureFlags from "@/core/utilities/featureFlags";
import { useChannelPartner } from "@/shared/account/composables/useChannelPartner";
import { EXTENDED_DEBOUNCE_IN_MS } from "@/shared/cart";
import { eventBus } from "@/shared/thorlabs/shared/event-bus";
import { useCheckoutFacade } from "../../composables/useCheckoutFacade";
import { useShippingMethod } from "../../composables/useShippingMethod";
import { currencyConfig } from "../../currency-settings";
import ShippingCo2Emissions from "./shipping-co2-emissions.vue";
import ShippingMethodAccountNumber from "./shipping-method-account-number.vue";
import ShippingMethodCompleted from "./shipping-method-completed.vue";
import ShippingMethodDhl from "./shipping-method-dhl.vue";
import ShippingMethodFedex from "./shipping-method-fedex.vue";
import ShippingMethodPackages from "./shipping-method-packages.vue";
import ShippingMethodUps from "./shipping-method-ups.vue";
import type { CurrencySettingsType } from "../../types";
import TlLoadingButton from "@/ui-kit/components/molecules/button/tl-loading-button.vue";

const props = defineProps<IProps>();
interface IProps {
  isCompletedStep?: boolean;
  isActiveStep?: boolean;
  nextStep?: string;
}

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { themeContext } = useThemeContext();
const showCo2Emissions = computed(
  () =>
    (new FeatureFlags(themeContext.value?.storeSettings?.modules ?? []).getValue(
      "Thorlabs.Commerce",
      "ShowCo2EmissionsWidget",
    ) as boolean) ?? false,
);
const {
  shipmentMethodTopNote,
  checkoutShipment,
  cartHasUkHeavyItems,
  cartHasVaporCells,
  shippingAccountNumberRecalculateTotals,
  changing,
  formattedCartWeight,
} = useCheckoutFacade();
const {
  selectedShippingMethod,
  dhlChoices,
  fedExChoices,
  upsChoices,
  weightExceeded,
  isOnlySpecialShippingMethod,
  isFedExExpediteIt,
  hasNoShippingChoices,
  fetchShippingChoices,
  applyShippingMethod,
} = useShippingMethod();
const currencySettings = currencyConfig[globals.currencyCode] as CurrencySettingsType;
const { isChannelPartner } = useChannelPartner();
const wantsFewerPackages = ref(checkoutShipment.value?.wantsFewerPackagesTL || false);
const isLoadingShippingChoices = ref(false);
const shippingChoiceCallError = ref(false);
const shippingAccountNumber = computed(() => checkoutShipment.value?.shippingAccountNumberTL || "");
const weightUnit = computed(() => (globals.currencyCode === "USD" ? "lbs" : "kg"));
const weightLimitExceeded = computed(() => {
  return t("shared.checkout.configurable_cart_weight_exceeded_case", [weightExceeded.value, weightUnit.value]);
});

onMounted(async () => {
  if (!isChannelPartner.value && !isOnlySpecialShippingMethod.value && props.isActiveStep) {
    isLoadingShippingChoices.value = true;
    await fetchShippingChoices();
    isLoadingShippingChoices.value = false;
  }
});

watch(
  () => props.isActiveStep,
  async (newVal) => {
    if (newVal && !isChannelPartner.value && !isOnlySpecialShippingMethod.value) {
      isLoadingShippingChoices.value = true;
      await fetchShippingChoices();
      isLoadingShippingChoices.value = false;
    }
  },
);

watch(
  () => selectedShippingMethod.value,
  async (newVal) => {
    if (newVal && globals.currencyCode === "USD") {
      await applyShippingMethod(true, wantsFewerPackages.value, shippingAccountNumberRecalculateTotals.value);
    } else {
      await applyShippingMethod(false, wantsFewerPackages.value, shippingAccountNumberRecalculateTotals.value);
    }
  },
);

const debouncedApplyForAccountNumber = useDebounceFn(async () => {
  const shouldCalculateUsdSalesTax = globals.currencyCode === "USD";
  // Do no trigger the update shippingMethod if both shippingAccountNumber and rateModeId ( shippingMethod / vendorId ) are not set
  if (!shippingAccountNumberRecalculateTotals.value && !selectedShippingMethod.value) {
    return;
  }
  await applyShippingMethod(
    shouldCalculateUsdSalesTax,
    wantsFewerPackages.value,
    shippingAccountNumberRecalculateTotals.value,
  );
}, EXTENDED_DEBOUNCE_IN_MS);

watch(
  () => shippingAccountNumberRecalculateTotals.value,
  () => {
    void debouncedApplyForAccountNumber();
  },
);

const nextStepHandle = async (callback: (result?: unknown) => void) => {
  try {
    await applyShippingMethod(false, wantsFewerPackages.value, shippingAccountNumberRecalculateTotals.value, true);

    if (props.nextStep) {
      await router.push({ name: props.nextStep, query: route.query, replace: true });

      // Wait for DOM update, then scroll next step into view with header offset
      requestAnimationFrame(() => {
        const activeStepEl = document.querySelector('[data-step="active"]') as HTMLElement;
        if (activeStepEl) {
          const headerOffset = 100; // sticky header height
          const elementPosition = activeStepEl.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      });
    }
  } finally {
    callback();
  }
};

eventBus.on("showShippingChoiceFormError", (payload) => {
  console.error(payload);
  shippingChoiceCallError.value = true;
});
eventBus.on("hideShippingChoiceFormError", () => {
  shippingChoiceCallError.value = false;
});

onUnmounted(() => {
  eventBus.off("showShippingChoiceFormError");
  eventBus.off("hideShippingChoiceFormError");
});
</script>

<style lang="scss">
.checkout-page {
  ul {
    @apply font-medium;
  }

  .delivery-option {
    @apply grid gap-1 md:gap-0;

    grid-template-columns: subgrid;
    grid-column: span 4;

    @media (max-width: theme("screens.sm-max")) {
      grid-column: span 2;
    }
  }
}
</style>
